import { type Ref, ref, watch } from 'vue';
import globalSettings from '../../../settings.default';
import { setCookie } from '../../../utils/cookie';
import { mergeSettings } from './mergeSettings';
import type { Settings } from './types';
import { defineStore } from 'pinia';
import { type RecursiveRequired } from '../../../@types/global';

interface UseSettingsStore {
  settings: Ref<RecursiveRequired<Settings>>;
  updateSettings: (newSettings: RecursiveRequired<Settings>) => void;
  initSettings: (newSettings: Settings) => void;
}

const useSettingsStore = defineStore('setting', (): UseSettingsStore => {
  // @ts-ignore
  const settings = ref(globalSettings) as Ref<RecursiveRequired<Settings>>;
  function updateSettings(newSettings: RecursiveRequired<Settings>) {
    settings.value = newSettings;
  }

  function updateTheme() {
    document.documentElement.setAttribute('data-theme',`${settings.value.theme.style}_${settings.value.theme.layoutStyle}_${settings.value.menu.menuMode}`);
  }

  function updateLayoutSizeClass() {
    const layoutSize = settings.value.theme.layoutSize;
    // 使用 data 属性替代 class，优先级更可靠
    document.documentElement.setAttribute('data-layout-size', layoutSize || 'loose');
  }

  function initSettings(newSettings: Settings) {
    updateSettings(mergeSettings(newSettings));
    // 默认主题
    if(settings.value.theme.style){
      updateTheme()
    }
    updateLayoutSizeClass();
  }

  watch(
    () => settings.value.theme.style,
    () => {
      if (settings.value.theme.style) {
        updateTheme()
      }
    },
  );

  watch(
    () => settings.value.theme.layoutStyle,
    () => {
      if (settings.value.theme.layoutStyle) {
        updateTheme()
      }
    },
  );

  watch(
    () => settings.value.menu.menuMode,
    () => {
      if (settings.value.menu.menuMode) {
        updateTheme()
      }
    },
  );

  watch(
    () => settings.value.theme.layoutSize,
    () => {
      if (settings.value.theme.layoutSize) {
        updateLayoutSizeClass();
      }
    },
  );

  watch(
    () => settings.value.system.systemCode,
    () => {
      setCookie({
        name: 'systemCode',
        value: settings.value.system.systemCode,
      });
    },
  );

  return {
    settings,
    updateSettings,
    initSettings,
  };
});

export default useSettingsStore;
